import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import Loader from 'src/shared/components/loader/Loader';
import { useLazyProjectSitesQuery } from 'src/app/projects/views/project-list/state/api/projectListGraphSlice';
import {
    useGetOrganisationQuery,
    useCreateOrganisationMutation,
    useCreateSiteMutation,
    useConvertGeoJSONToGeographyMutation,
    useCreateGeographyMutation,
    type SiteStatus,
} from './state/api/organisationEngineSlice';
import { fetchAllDraftSitesForImport, useLazyGetRiskPortfolioImportPreloadQuery } from './state/api/riskPortfolioEngineGraphSlice';
import SetupWizard from './components/setup-wizard/SetupWizard';
import useImportSites from './hooks/useImportSites';
import OrgSiteManagement from './views/site-management/OrgSiteManagement';
import styles from './RiskPortfolioAdmin.module.scss';

type RiskPortfolioAdminProps = {
    currentCustomerId: string;
    currentCustomerName: string;
    projects: Array<{ projectId: string; projectName: string }>;
};

const RiskPortfolioAdmin = ({ currentCustomerId, currentCustomerName, projects }: RiskPortfolioAdminProps) => {
    const [createError, setCreateError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<SiteStatus>('active');
    const [wizardActive, setWizardActive] = useState(false);
    const [wizardComplete, setWizardComplete] = useState(false);

    const { data: organisation, isLoading, error } = useGetOrganisationQuery(currentCustomerId, { skip: !currentCustomerId });

    const [createOrganisation, { isLoading: isCreating }] = useCreateOrganisationMutation();
    const [createSiteRaw] = useCreateSiteMutation();
    const [convertGeoJSONToGeographyRaw] = useConvertGeoJSONToGeographyMutation();
    const [createGeographyRaw] = useCreateGeographyMutation();
    const [triggerProjectSites] = useLazyProjectSitesQuery();
    const [triggerImportPreload] = useLazyGetRiskPortfolioImportPreloadQuery();

    const noOrgExists = !isLoading && !error && organisation === null;
    const showWizard = (noOrgExists || wizardActive) && !wizardComplete;

    const createSite = (req: { bcId: string; body: { name: string; business_central_id: string } }) => createSiteRaw(req).unwrap();
    const fetchProjectSites = (projectId: string) => triggerProjectSites({ projectId }).unwrap();
    const convertGeoJSONToGeography = (input: Parameters<typeof convertGeoJSONToGeographyRaw>[0]) =>
        convertGeoJSONToGeographyRaw(input).unwrap();
    const createGeography = (req: Parameters<typeof createGeographyRaw>[0]) => createGeographyRaw(req).unwrap();
    const fetchExistingDraftSites = (bcId: string) => fetchAllDraftSitesForImport(args => triggerImportPreload(args).unwrap(), bcId);

    const {
        importSites,
        isImporting,
        progress: importProgress,
        result: importResult,
    } = useImportSites({
        createSite,
        fetchProjectSites,
        convertGeoJSONToGeography,
        createGeography,
        fetchExistingDraftSites,
    });

    const handleCreateOrganisation = async () => {
        setCreateError(null);
        try {
            await createOrganisation({
                business_central_id: currentCustomerId,
                name: currentCustomerName,
            }).unwrap();
            setWizardActive(true);
        } catch {
            setCreateError('Failed to create organisation. Please try again.');
        }
    };

    const handleImportSites = async () => {
        await importSites(currentCustomerId, projects);
    };

    const handleWizardComplete = () => {
        setWizardActive(false);
        setWizardComplete(true);
        setStatusFilter('draft');
    };

    if (!currentCustomerId || isLoading) {
        return (
            <div className={styles.loadingContainer} data-testid='risk-portfolio-loading'>
                <Loader />
            </div>
        );
    }

    if (error) {
        return (
            <div className='flex flex-col items-center justify-center py-24 gap-6'>
                <div className='flex items-center justify-center w-20 h-20 rounded-full bg-red-0'>
                    <AlertCircle className='w-10 h-10 text-red-100' />
                </div>
                <div className='flex flex-col items-center gap-2 max-w-md text-center'>
                    <h2 className='text-2xl font-semibold text-grey-100'>Error</h2>
                    <p className='text-base text-grey-70'>Failed to check organisation status. Please try again later.</p>
                </div>
            </div>
        );
    }

    if (showWizard) {
        return (
            <SetupWizard
                customerName={currentCustomerName}
                currentCustomerId={currentCustomerId}
                onCreateOrganisation={handleCreateOrganisation}
                isCreatingOrg={isCreating}
                createOrgError={createError}
                projects={projects}
                onImportSites={handleImportSites}
                isImporting={isImporting}
                importProgress={importProgress}
                importResult={importResult ?? null}
                onComplete={handleWizardComplete}
            />
        );
    }

    if (organisation) {
        return (
            <OrgSiteManagement
                currentCustomerId={currentCustomerId}
                projects={projects}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
            />
        );
    }

    return null;
};

export default RiskPortfolioAdmin;
